import { tipoMovimiento } from './valoracion';
/**
 * Transforma arrays de datos crudos en transacciones normalizadas para la UI.
 * Sin dependencias de React — testeable de forma aislada.
 *
 * @param {Object} params
 * @param {Array}  params.movimientos  ENTRADA / VENTA / SALIDA del día
 * @param {Array}  params.gastos       Gastos activos del día
 * @param {Array}  params.ajustes      Ajustes del día
 * @param {Array}  params.traspasos    Traspasos QR del día
 * @param {Array}  params.productos    Catálogo completo de productos
 * @returns {Array} Transacciones ordenadas cronológicamente (más antiguas primero)
 */
export const construirTransaccionesDelDia = ({ movimientos, gastos, ajustes, traspasos, productos }) => {
  const transacciones = [];

  (movimientos || []).forEach(mov => {
    const producto = (productos || []).find(p => p.id === mov.productoId);
    const etiqueta =
      mov.tipo === 'ENTRADA' ? '📦 Compra' : tipoMovimiento(mov) === 'VENTA' ? '🛍️ Venta' : '📤 Salida';

    transacciones.push({
      id: mov.id,
      tipo: 'MOVIMIENTO',
      subtipo: tipoMovimiento(mov),
      fecha: new Date(mov.fecha),
      fechaISO: mov.fecha,
      display: `${etiqueta}: ${producto?.nombre || 'N/A'}`,
      cantidad: mov.cantidad,
      precio: mov.precio,
      metodoPago: mov.metodoPago,
      objOriginal: mov,
      puedeEditar: !mov.anulado && mov.origenOperacion !== 'LIMPIEZA' && !mov.operacionId,
      esMovimiento: true,
      anulado: !!mov.anulado,
    });
  });

  (gastos || []).forEach(gasto => {
    transacciones.push({
      id: gasto.id,
      tipo: 'GASTO',
      subtipo: gasto.categoriaId,
      fecha: new Date(gasto.fecha),
      fechaISO: gasto.fecha,
      display: `💸 Gasto: ${gasto.descripcion || 'Sin descripción'}`,
      monto: gasto.monto,
      metodoPago: gasto.metodoPago,
      objOriginal: gasto,
      puedeEditar: gasto.estado !== 'ANULADO',
      esGasto: true,
      anulado: gasto.estado === 'ANULADO',
    });
  });

  (ajustes || []).forEach(ajuste => {
    transacciones.push({
      id: ajuste.id,
      tipo: 'AJUSTE',
      subtipo: ajuste.tipo,
      fecha: new Date(ajuste.fecha),
      fechaISO: ajuste.fecha,
      display: `${ajuste.tipo === 'SOBRANTE' ? '⬆️' : '⬇️'} Ajuste: ${ajuste.descripcion || 'Sin descripción'}`,
      monto: ajuste.monto,
      metodoPago: ajuste.metodoPago || 'EFECTIVO',
      objOriginal: ajuste,
      puedeEditar: !ajuste.anulado && !ajuste.arqueoDiaId,
      esAjuste: true,
      anulado: !!ajuste.anulado,
    });
  });

  (traspasos || []).forEach(traspaso => {
    const esCAJaQR = traspaso.direccion === 'CAJ_A_QR';
    const desde = esCAJaQR ? 'Efectivo' : 'QR';
    const hacia = esCAJaQR ? 'QR' : 'Efectivo';

    transacciones.push({
      id: traspaso.id,
      tipo: 'TRASPASO',
      subtipo: traspaso.direccion,
      fecha: new Date(traspaso.fecha),
      fechaISO: traspaso.fecha,
      display: `🔄 Traspaso: $${traspaso.monto.toFixed(2)} (${desde} → ${hacia})`,
      monto: traspaso.monto,
      metodoPago: traspaso.direccion === 'CAJ_A_QR' ? 'EFECTIVO' : 'QR',
      objOriginal: traspaso,
      puedeEditar: !traspaso.anulado,
      esTraspaso: true,
      anulado: !!traspaso.anulado,
    });
  });

  return transacciones.sort((a, b) => a.fecha - b.fecha);
};
